/**
 * Correcting a time entry that is already logged.
 *
 * `updateTimeEntry` has been exported from `lib/projects.ts` since the module
 * was written and had exactly zero callers: the only repair the CRM offered for
 * a mistyped hour was to delete the row and type it again, which loses who
 * logged it and when. This dialog is that missing caller.
 *
 * It sends ONLY the fields the operator actually changed. That is not tidiness:
 * `startedAt` re-sent unchanged would arrive as midnight (`toIso` has no clock
 * to keep) and quietly reshuffle the order of everything logged that day, since
 * the list is ordered by that column. The same reasoning covers the rest - a
 * PATCH that writes back what it read is a PATCH that can undo somebody else's
 * edit made between the load and the save.
 *
 * An invoiced entry never reaches this dialog: `TimeEntries` shows the reason in
 * place of the menu instead. The 409 is still handled here, because the row on
 * screen was drawn before somebody in the next chair pressed "invoice time".
 *
 * WHOSE HOURS THESE ARE is correctable here and, unlike the add form, it can be
 * emptied. Every one of the 185 imported entries arrives with `userId: ""`, so
 * this dialog is where an owner gets put on an hour that never had one - and a
 * field that can fill a value in has to be able to take it back out, or a slip
 * of the mouse becomes permanent. An emptied picker sends the empty string
 * EXPLICITLY rather than dropping the key: the route's fallback fires on an
 * absent field and would hand the entry to whoever is signed in, which is the
 * one answer the operator has just said is wrong.
 *
 * The shared `UserPicker` rather than the add form's menu, and the difference is
 * the dress, not the decision: this is a form column, which is what the picker's
 * search box and chip are drawn for.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { updateTimeEntry, type TimeEntry } from "../../../lib/projects";
import { Btn, Modal, useToast } from "../../ui";
import { UserPicker } from "../_shared/UserPicker";
import { toDateInput, toHoursInput, toIso } from "./lib";
import { TaskSelect, type TaskOption } from "./TimeEntryForm";

export function TimeEntryEditModal({
  entry,
  tasks,
  onClose,
  onSaved,
}: {
  entry: TimeEntry;
  /** THIS project's tasks - the same scoping rule the add form documents. */
  tasks: TaskOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();

  const startedOn = toDateInput(entry.startedAt);
  const [hours, setHours] = useState(toHoursInput(entry.minutes));
  const [note, setNote] = useState(entry.note ?? "");
  const [taskId, setTaskId] = useState(entry.taskId ?? "");
  const [date, setDate] = useState(startedOn);
  // An empty string is stored as "nobody", and the picker's own word for that is
  // null. Converted at the edge in both directions, so the comparison at submit
  // time is between two of the SAME thing.
  const [userId, setUserId] = useState<string | null>(entry.userId || null);
  const [billable, setBillable] = useState(entry.billable);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const h = parseFloat(hours);
    if (!h || h <= 0 || busy) return;

    const patch: Record<string, unknown> = {};
    const minutes = Math.round(h * 60);
    if (minutes !== entry.minutes) patch.minutes = minutes;
    const trimmed = note.trim();
    if (trimmed !== (entry.note ?? "")) patch.note = trimmed;
    if (billable !== entry.billable) patch.billable = billable;
    // Moved to another task, or given one for the first time. Never cleared:
    // see `TaskSelect`, which is why the picker offers no empty row here.
    if (taskId && taskId !== entry.taskId) patch.taskId = taskId;
    // Given an owner, moved to another one, or taken off the one it had. The
    // empty string is a value the route accepts, so all three are one comparison
    // - and an untouched field still produces nothing to send.
    const owner = userId ?? "";
    if (owner !== entry.userId) patch.userId = owner;
    // Only when the DAY moved. A cleared date is not a change either - a time
    // entry has no state in which it happened on no day.
    if (date && date !== startedOn) {
      const iso = toIso(date);
      if (iso) patch.startedAt = iso;
    }

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    try {
      await updateTimeEntry(entry.id, patch);
      onSaved();
      onClose();
    } catch (err) {
      const e2 = err as { body?: { error?: string } };
      if (e2.body?.error === "entry_invoiced") {
        // Invoiced between the load and the save. Name that rather than
        // "something went wrong", and reload so the row comes back locked.
        toast(t("projects.time.invoicedLocked"));
        onSaved();
        onClose();
        return;
      }
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = t("projects.time.editTitle");

  return (
    <Modal title={title} label={title} onClose={onClose} className="max-w-[460px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="crm-lab">{t("projects.hours")} *</span>
            <label className="crm-inp">
              <input
                type="number"
                step="0.25"
                min="0"
                inputMode="decimal"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                aria-label={t("projects.hours")}
                autoFocus
                required
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("projects.time.date")}</span>
            <DatePicker value={date} onChange={setDate} locale={locale} placeholder={t("projects.time.date")} />
          </div>
        </div>

        <div>
          <span className="crm-lab">{t("projects.time.task")}</span>
          <TaskSelect
            value={taskId}
            onChange={setTaskId}
            tasks={tasks}
            variant="form"
            allowNone={!entry.taskId}
          />
        </div>

        {/* Empty says so in a sentence, the way the project form's team field
            does. A search box with no chip under it reads as a control that
            failed to load; on this dialog that would be the reading on every
            entry the CRM currently holds, since none of them names anybody. */}
        <div>
          <span className="crm-lab">{t("projects.loggedBy")}</span>
          <UserPicker value={userId} onChange={setUserId} />
          {userId ? null : (
            <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.unassigned")}</p>
          )}
        </div>

        <div>
          <span className="crm-lab">{t("projects.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("projects.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        <label className="crm-inp cursor-pointer">
          <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
          <span className="text-[13.5px]">{t("projects.billable")}</span>
        </label>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !hours}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
